import React, { useState, useCallback } from 'react';
import { Search, X } from 'lucide-react';
import Input from './Input';
import { cn } from '../../utils/cn';

export interface SearchInputProps {
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  onSearch?: (value: string) => void;
  onClear?: () => void;
  className?: string;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'filled' | 'outlined';
  debounceMs?: number;
  showClearButton?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const SearchInput: React.FC<SearchInputProps> = ({
  placeholder = 'Search...',
  value: controlledValue,
  onChange,
  onSearch,
  onClear,
  className,
  disabled = false,
  size = 'md',
  variant = 'default',
  debounceMs = 300,
  showClearButton = true,
  leftIcon,
  rightIcon,
}) => {
  const [internalValue, setInternalValue] = useState('');
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    
    if (!isControlled) {
      setInternalValue(newValue);
    }
    
    onChange?.(newValue);

    // Debounce search
    if (onSearch && debounceMs > 0) {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      
      const timer = setTimeout(() => {
        onSearch(newValue);
      }, debounceMs);
      
      setDebounceTimer(timer);
    } else if (onSearch) {
      onSearch(newValue);
    }
  }, [isControlled, onChange, onSearch, debounceMs, debounceTimer]);

  const handleClear = useCallback(() => {
    if (!isControlled) {
      setInternalValue('');
    }
    
    onChange?.('');
    onSearch?.('');
    onClear?.();
  }, [isControlled, onChange, onSearch, onClear]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSearch?.(value);
    }
  }, [onSearch, value]);

  const showClear = showClearButton && value.length > 0;

  return (
    <div className={cn('relative', className)}>
      <Input
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        size={size}
        variant={variant}
        leftIcon={leftIcon || <Search size={16} />}
        rightIcon={
          showClear ? (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-full transition-colors"
              disabled={disabled}
            >
              <X size={14} className="text-gray-400" />
            </button>
          ) : rightIcon
        }
        className="pr-10"
      />
    </div>
  );
}; 